import type { Note } from '@prisma/client';
import { themeQuartz } from 'ag-grid-community';
import type { ProcessCellForExportParams, SideBarDef } from 'ag-grid-enterprise';
import { AllEnterpriseModule, LicenseManager, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import { formatUSD, titlifyEnum } from '@utils/stringHelpers';
import { escapeCsvFormula } from '@utils/tabularExport';
import { useCallback, useMemo } from 'react';
import type { DeliveryChipDatum } from './gridFormatters';

// Any data manipulation that needs to be done for CSV/Excel can be done here
export const processCellCallback = (params: ProcessCellForExportParams<any, any>) => {
	const field = params.column.getUserProvidedColDef()?.field;
	const colId = params.column.getColId();

	if (field === 'Notes' && Array.isArray(params.value)) {
		const notes: Note[] = params.value;
		return notes.map((note) => note.content).join('; ');
	}

	if (field === 'transactions' && Array.isArray(params.value)) {
		const transactions: { id: number; amount: number }[] = params.value;
		return transactions.map((t) => formatUSD(t.amount)).join(', ');
	}

	if ((colId === 'expectedArrivalDate' || colId === 'actualArrivalDate') && Array.isArray(params.value)) {
		const deliveries = params.value as DeliveryChipDatum[];
		return deliveries
			.filter((d) => d[colId] != null)
			.map((d) => {
				const date = new Date(d[colId]!).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
				return `${d.deliveryNumber} · ${date} (${titlifyEnum(d.status)})`;
			})
			.join('; ');
	}

	return params.value;
};

/**
 * CSV only — Excel exports write strings as text cells, so they need no escaping and should not carry the
 * apostrophe.
 */
export const csvProcessCellCallback = (params: ProcessCellForExportParams) => {
	const value = processCellCallback(params);
	return typeof value === 'string' ? escapeCsvFormula(value) : value;
};

export class GridUtilities {
	constructor(private gridRef: React.RefObject<AgGridReact | null>) {}

	onExport = useCallback((type: 'CSV' | 'XLS') => {
		if (type === 'CSV') {
			this.gridRef.current?.api.exportDataAsCsv({ processCellCallback: csvProcessCellCallback });
		} else {
			this.gridRef.current?.api.exportDataAsExcel({ processCellCallback });
		}
	}, []);

	onExportFiltered = useCallback((type: 'CSV' | 'XLS') => {
		if (type === 'CSV') {
			this.gridRef.current?.api.exportDataAsCsv({
				shouldRowBeSkipped: (params) => !params.node.displayed,
				processCellCallback: csvProcessCellCallback,
			});
		} else {
			this.gridRef.current?.api.exportDataAsExcel({
				shouldRowBeSkipped: (params) => !params.node.displayed,
				processCellCallback,
			});
		}
	}, []);

	sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => {
		return {
			toolPanels: ['filters'],
		};
	}, []);
}

export const setLicenseKey = () => {
	LicenseManager.setLicenseKey(
		'Using_this_{AG_Grid}_Enterprise_key_{AG-063926}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{River_Prime}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{River_Prime}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{River_Prime}_need_to_be_licensed___{River_Prime}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{23_July_2025}____[v3]_[01]_MTc1MzIyNTIwMDAwMA==1200d27c6f62377b36b8f92b7c13fe53'
	);

	// TODO: Use Module Selector to cut down bundle size https://www.ag-grid.com/javascript-data-grid/modules/
	ModuleRegistry.registerModules([AllEnterpriseModule]);

	// TODO: Migrate to theming API https://www.ag-grid.com/javascript-data-grid/theming-migration/
	provideGlobalGridOptions({ theme: 'legacy' });
};

export const agGridTheme = themeQuartz.withParams({
	accentColor: '#0BA3D5',
	borderColor: '#EDEEF3',
	browserColorScheme: 'light',
	cellTextColor: '#1A262D',
	columnBorder: true,
	fontFamily: {
		googleFont: 'Inter',
	},
	headerFontFamily: {
		googleFont: 'Inter',
	},
	headerFontSize: 14,
	headerTextColor: '#1A262D',
	spacing: 10,
});
